import { useCallback, useEffect, useRef, useState, type FormEvent, type MutableRefObject } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import type { Catalog, CreateCatalogRequest, UpdateCatalogRequest, EndpointTargetMode } from '@/types/catalog';
import type { Endpoint } from '@/types/endpoint';
import { api } from '@/lib/api';
import { additionalPortErrors } from '@/lib/catalogForm';
import { useDialogFocusRestore } from '@/lib/hooks/useDialogFocusRestore';

interface CatalogDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  catalog: Catalog | null;
  onSubmit: (data: CreateCatalogRequest | UpdateCatalogRequest) => Promise<void>;
}

/**
 * Shell always mounted; the form body mounts only while open, so fields reset on every open and the
 * form state is initialised from `catalog` without an effect. Close is blocked while saving.
 */
export function CatalogDialog({ open, onOpenChange, catalog, onSubmit }: CatalogDialogProps) {
  const submittingRef = useRef(false);
  const { captureOpener, restoreFocus } = useDialogFocusRestore();
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && submittingRef.current) return;
        onOpenChange(next);
      }}
    >
      {open ? (
        <CatalogFormBody
          key={catalog ? `edit:${catalog.id}` : 'create'}
          catalog={catalog}
          onClose={() => onOpenChange(false)}
          onSubmit={onSubmit}
          submittingRef={submittingRef}
          captureOpener={captureOpener}
          restoreFocus={restoreFocus}
        />
      ) : null}
    </Dialog>
  );
}

interface FormBodyProps {
  catalog: Catalog | null;
  onClose: () => void;
  onSubmit: (data: CreateCatalogRequest | UpdateCatalogRequest) => Promise<void>;
  submittingRef: MutableRefObject<boolean>;
  captureOpener: () => void;
  restoreFocus: (event: Event) => void;
}

function CatalogFormBody({ catalog, onClose, onSubmit, submittingRef, captureOpener, restoreFocus }: FormBodyProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [name, setName] = useState(catalog?.name ?? '');
  const [description, setDescription] = useState(catalog?.description ?? '');
  const [exposePort, setExposePort] = useState(catalog?.expose_port || 0);
  const [additionalPorts, setAdditionalPorts] = useState<{ port: number; prefix: string }[]>(catalog?.additional_ports ?? []);
  const [targetMode, setTargetMode] = useState<EndpointTargetMode>(catalog?.target_mode ?? null);
  const [lockedEndpoints, setLockedEndpoints] = useState<number[]>(catalog?.locked_endpoints ?? []);
  const [endpoints, setEndpoints] = useState<Endpoint[] | null>(null);
  const [endpointsError, setEndpointsError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stable callback ref: focuses the alert when it mounts, never on unrelated re-renders.
  const alertRef = useCallback((el: HTMLParagraphElement | null) => {
    el?.focus();
  }, []);

  // Locked mode needs the endpoint list. Only 'up' endpoints can be chosen.
  useEffect(() => {
    if (targetMode !== 'locked') return;
    let cancelled = false;
    api.endpoints
      .listAll()
      .then((data) => {
        if (!cancelled) setEndpoints(data.filter((ep) => ep.status === 'up'));
      })
      .catch((err: unknown) => {
        if (!cancelled) setEndpointsError(err instanceof Error && err.message ? err.message : 'Failed to load endpoints');
      });
    return () => {
      cancelled = true;
    };
  }, [targetMode]);

  const lockedInvalid = targetMode === 'locked' && lockedEndpoints.length === 0;
  const [portsChecked, setPortsChecked] = useState(false);
  const portErrors = additionalPortErrors(additionalPorts);
  // A fresh empty row stays quiet until it gets input or a save is attempted.
  const shownPortErrors = portErrors.map((msg, i) => (portsChecked || additionalPorts[i].port || additionalPorts[i].prefix ? msg : undefined));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    setError(null);

    if (lockedInvalid) {
      setError('Locked mode requires at least one endpoint to be selected.');
      return;
    }
    if (portErrors.some(Boolean)) {
      setPortsChecked(true);
      setError('Fix the additional exposed ports before saving.');
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    try {
      await onSubmit({
        name,
        description,
        expose_port: exposePort,
        additional_ports: additionalPorts,
        target_mode: targetMode,
        locked_endpoints: lockedEndpoints.length > 0 ? lockedEndpoints : undefined,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Failed to save catalog');
      setSubmitting(false);
    } finally {
      submittingRef.current = false;
    }
  };

  const blockWhileSubmitting = (e: Event) => {
    if (submittingRef.current) e.preventDefault();
  };

  const updatePort = (index: number, patch: Partial<{ port: number; prefix: string }>) =>
    setAdditionalPorts((ports) => ports.map((p, i) => (i === index ? { ...p, ...patch } : p)));

  return (
    <DialogContent
      className="sm:max-w-lg"
      onOpenAutoFocus={(e) => {
        captureOpener();
        e.preventDefault();
        cancelRef.current?.focus();
      }}
      onCloseAutoFocus={restoreFocus}
      onEscapeKeyDown={blockWhileSubmitting}
      onPointerDownOutside={blockWhileSubmitting}
      onInteractOutside={blockWhileSubmitting}
    >
      <DialogHeader>
        <DialogTitle>{catalog ? 'Edit catalog' : 'Create catalog'}</DialogTitle>
        <DialogDescription>{catalog ? 'Update the catalog details below.' : 'Create a new application catalog.'}</DialogDescription>
      </DialogHeader>
      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
        {error ? (
          <p ref={alertRef} tabIndex={-1} role="alert" className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive outline-none [overflow-wrap:anywhere]">
            {error}
          </p>
        ) : null}

        <div className="space-y-2">
          <label htmlFor="catalogName" className="text-sm font-medium">
            Name
          </label>
          <Input id="catalogName" value={name} onChange={(e) => setName(e.target.value)} required placeholder="My Application" />
        </div>

        <div className="space-y-2">
          <label htmlFor="catalogDescription" className="text-sm font-medium">
            Description
          </label>
          <Input id="catalogDescription" value={description} onChange={(e) => setDescription(e.target.value)} required placeholder="Application description" />
        </div>

        <div className="space-y-2">
          <label htmlFor="exposePort" className="text-sm font-medium">
            Expose port (Traefik routing)
          </label>
          <Input id="exposePort" type="number" value={exposePort} onChange={(e) => setExposePort(parseInt(e.target.value) || 0)} placeholder="0" />
          <p className="text-xs text-muted-foreground">
            Container port to expose via subdomain routing. Set to 0 if no public routing is needed. Requires a Traefik deployment on the target endpoint.
          </p>
        </div>

        <fieldset className="space-y-2">
          <div className="flex items-center justify-between">
            <legend className="text-sm font-medium">Additional exposed ports</legend>
            <Button type="button" variant="outline" size="sm" onClick={() => setAdditionalPorts([...additionalPorts, { port: 0, prefix: '' }])}>
              Add port
            </Button>
          </div>

          {additionalPorts.map((p, index) => (
            <div key={index} className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="grid flex-1 grid-cols-2 gap-2">
                  <Input
                    type="number"
                    aria-label={`Additional port ${index + 1} number`}
                    aria-invalid={shownPortErrors[index] ? true : undefined}
                    aria-describedby={shownPortErrors[index] ? `additionalPortError${index}` : undefined}
                    value={p.port || ''}
                    onChange={(e) => updatePort(index, { port: parseInt(e.target.value) || 0 })}
                    placeholder="Port (e.g. 8080)"
                  />
                  <Input
                    aria-label={`Additional port ${index + 1} prefix`}
                    aria-invalid={shownPortErrors[index] ? true : undefined}
                    aria-describedby={shownPortErrors[index] ? `additionalPortError${index}` : undefined}
                    value={p.prefix}
                    onChange={(e) => updatePort(index, { prefix: e.target.value.toLowerCase() })}
                    maxLength={15}
                    placeholder="Prefix (e.g. api)"
                  />
                </div>
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="h-9 w-9"
                  aria-label={`Remove additional port ${index + 1}`}
                  onClick={() => setAdditionalPorts(additionalPorts.filter((_, i) => i !== index))}
                >
                  &times;
                </Button>
              </div>
              {shownPortErrors[index] ? (
                <p id={`additionalPortError${index}`} className="text-xs text-destructive">
                  {shownPortErrors[index]}
                </p>
              ) : null}
            </div>
          ))}

          {additionalPorts.length > 0 ? (
            <p className="mt-1 text-xs text-muted-foreground">
              A prefix generates a domain like `[prefix]-[subdomain]` for its port: up to 15 lowercase letters, digits or &quot;-&quot;, not at the start or end.
            </p>
          ) : null}
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="text-sm font-medium">Target endpoint mode</legend>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="radio"
                id="target-auto"
                name="target-mode"
                checked={targetMode === null || targetMode === 'auto'}
                onChange={() => {
                  setTargetMode(null);
                  setLockedEndpoints([]);
                }}
                className="size-4"
              />
              <label htmlFor="target-auto" className="text-sm">
                Auto (default): select from all eligible endpoints
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input type="radio" id="target-locked" name="target-mode" checked={targetMode === 'locked'} onChange={() => setTargetMode('locked')} className="size-4" />
              <label htmlFor="target-locked" className="text-sm">
                Locked: restrict to specific endpoints
              </label>
            </div>
          </div>

          {targetMode === 'locked' ? (
            <div className="space-y-2 border-l-2 border-muted pl-6">
              <p className="text-xs text-muted-foreground">Select endpoints that can deploy apps from this catalog.</p>
              {endpointsError ? (
                <p role="alert" className="text-sm text-destructive">
                  {endpointsError}
                </p>
              ) : endpoints === null ? (
                <p role="status" className="text-sm text-muted-foreground">
                  Loading endpoints…
                </p>
              ) : endpoints.length === 0 ? (
                <p className="text-sm text-muted-foreground">No endpoints are up right now, so none can be selected.</p>
              ) : (
                <div className="max-h-48 space-y-1 overflow-y-auto rounded border p-2">
                  {endpoints.map((ep) => (
                    <label key={ep.id} className="flex cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        checked={lockedEndpoints.includes(ep.id)}
                        onChange={(e) =>
                          setLockedEndpoints(e.target.checked ? [...lockedEndpoints, ep.id] : lockedEndpoints.filter((id) => id !== ep.id))
                        }
                        className="size-4 accent-primary"
                      />
                      <span className="text-sm">{ep.name}</span>
                      <span className="text-xs text-muted-foreground">(ID: {ep.id})</span>
                    </label>
                  ))}
                </div>
              )}
              {lockedInvalid ? <p className="text-xs text-destructive">At least one endpoint must be selected when using locked mode.</p> : null}
            </div>
          ) : null}
        </fieldset>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button ref={cancelRef} type="button" variant="outline" aria-disabled={submitting} onClick={() => !submitting && onClose()}>
            Cancel
          </Button>
          <Button type="submit" aria-disabled={submitting || lockedInvalid} aria-busy={submitting}>
            {submitting ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
            {submitting ? 'Saving…' : catalog ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
